<script setup>
// Coluna: lista de elementos arrastável (grupo "elements"). Recebe item
// novo do painel ({ __element }) ou elemento vindo de outra coluna.
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import {
  themeColor,
  paddingCss,
  borderStyle,
  columnInnerFor,
} from './canvasStyles';
import CanvasElement from './elements/CanvasElement.vue';

const props = defineProps({
  column: { type: Object, required: true },
  row: { type: Object, required: true },
  stacked: { type: Boolean, default: false },
});
const ed = inject('emailEditor');
const { t } = useI18n();
const theme = computed(() => ed.template.value?.theme || {});
const cs = computed(() => props.column.style || {});
const inner = computed(() =>
  columnInnerFor(
    ed.doc.value,
    props.row,
    props.column,
    ed.device.value,
    props.stacked
  )
);
// O servidor limita a imagem pelos números do computador, mesmo no celular.
const desktopInner = computed(() =>
  columnInnerFor(ed.doc.value, props.row, props.column, 'desktop', false)
);
// Fundo que o servidor usa para decidir o contraste automático do texto.
const bgValue = computed(
  () =>
    cs.value.background ||
    props.row.style?.background ||
    ed.doc.value.settings?.background_inner ||
    null
);
const selected = computed(() => ed.selectedId.value === props.column.id);
const highlighted = computed(
  () => !!ed.highlightIds?.value?.includes(props.column.id)
);
const outlineClass = computed(() => {
  if (selected.value) return 'outline-dashed outline-n-brand';
  if (highlighted.value) return 'outline outline-n-amber-9';
  return '';
});
const VALIGN = {
  top: 'justify-start',
  middle: 'justify-center',
  bottom: 'justify-end',
};

const colStyle = computed(() => ({
  flexBasis: props.stacked ? '100%' : `${props.column.width}%`,
  backgroundColor: themeColor(cs.value.background, theme.value) || undefined,
  padding: paddingCss(cs.value.padding),
  borderRadius: cs.value.radius ? `${cs.value.radius}px` : undefined,
  ...borderStyle(cs.value.border, theme.value),
}));

/* eslint-disable no-underscore-dangle */
const onChange = evt => {
  if (evt.added) {
    const item = evt.added.element;
    if (item.__element) {
      ed.addElement(item.__element, {
        columnId: props.column.id,
        index: evt.added.newIndex,
      });
    } else if (item.__saved) {
      ed.insertSaved(item.__saved, {
        columnId: props.column.id,
        index: evt.added.newIndex,
      });
    } else {
      ed.moveElement(item.id, props.column.id, evt.added.newIndex);
    }
  }
  if (evt.moved) {
    ed.moveElement(evt.moved.element.id, props.column.id, evt.moved.newIndex);
  }
};
</script>

<template>
  <div
    :data-column="column.id"
    class="flex min-w-0 shrink-0 grow-0 flex-col outline-1 -outline-offset-1"
    :class="[VALIGN[column.valign] || 'justify-start', outlineClass]"
    :data-highlight="highlighted || undefined"
    :style="colStyle"
    @click.stop="ed.select(column.id)"
  >
    <Draggable
      :model-value="column.elements"
      item-key="id"
      :group="{ name: 'elements', pull: true, put: ['elements'] }"
      ghost-class="opacity-40"
      :animation="180"
      class="flex min-h-[48px] flex-col"
      @change="onChange"
    >
      <template #item="{ element }">
        <CanvasElement
          :element="element"
          :inner="inner"
          :desktop-inner="desktopInner"
          :bg-value="bgValue"
        />
      </template>
      <template #footer>
        <div
          v-if="!column.elements.length"
          class="m-1 flex min-h-[64px] items-center justify-center rounded-md border-2 border-dashed border-n-brand/30 px-2 text-center text-xs text-n-slate-10"
        >
          {{ t('EMAIL_TEMPLATES.EDITOR.CANVAS.EMPTY_COLUMN') }}
        </div>
      </template>
    </Draggable>
  </div>
</template>
